import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, AlignLeft, AlignCenter, AlignRight } from "lucide-react";

const SECTIONS = [
  {
    key: "home",
    label: "Inicio",
    fields: [
      { k: "hero_overline",       label: "Hero — Overline (línea pequeña arriba)", type: "text" },
      { k: "hero_title",          label: "Hero — Título principal",                  type: "textarea" },
      { k: "hero_sub",            label: "Hero — Subtítulo",                          type: "textarea" },
      { k: "featured_overline",   label: "Destacadas — Overline",                    type: "text" },
      { k: "featured_title",      label: "Destacadas — Título",                      type: "textarea" },
      { k: "cat_rural_title",     label: "Categoría Rural — Título",                 type: "text" },
      { k: "cat_rural_desc",      label: "Categoría Rural — Descripción",            type: "textarea" },
      { k: "cat_urban_title",     label: "Categoría Urbano — Título",                type: "text" },
      { k: "cat_urban_desc",      label: "Categoría Urbano — Descripción",           type: "textarea" },
      { k: "cat_industrial_title",label: "Categoría Industrial — Título",            type: "text" },
      { k: "cat_industrial_desc", label: "Categoría Industrial — Descripción",       type: "textarea" },
    ],
  },
  {
    key: "about",
    label: "Quiénes Somos",
    fields: [
      { k: "hero_overline",    label: "Hero — Overline",                         type: "text" },
      { k: "hero_line1",       label: "Hero — Línea 1 (blanco)",                 type: "text" },
      { k: "hero_line2",       label: "Hero — Línea 2 (dorado, itálica)",        type: "text" },
      { k: "manifesto_overline",label: "Manifiesto — Overline",                  type: "text" },
      { k: "manifesto",        label: "Manifiesto — Texto completo (multilínea)", type: "longtext" },
      { k: "values_overline",  label: "Valores — Overline",                      type: "text" },
      { k: "values_title",     label: "Valores — Título",                        type: "textarea" },
      { k: "value1_title",     label: "Valor 1 — Título",                        type: "text" },
      { k: "value1_desc",      label: "Valor 1 — Descripción",                   type: "textarea" },
      { k: "value2_title",     label: "Valor 2 — Título",                        type: "text" },
      { k: "value2_desc",      label: "Valor 2 — Descripción",                   type: "textarea" },
      { k: "value3_title",     label: "Valor 3 — Título",                        type: "text" },
      { k: "value3_desc",      label: "Valor 3 — Descripción",                   type: "textarea" },
      { k: "value4_title",     label: "Valor 4 — Título",                        type: "text" },
      { k: "value4_desc",      label: "Valor 4 — Descripción",                   type: "textarea" },
      { k: "stat1_value",      label: "Métrica 1 — Valor (ej. +15)",             type: "text" },
      { k: "stat1_label",      label: "Métrica 1 — Etiqueta",                    type: "text" },
      { k: "stat2_value",      label: "Métrica 2 — Valor",                       type: "text" },
      { k: "stat2_label",      label: "Métrica 2 — Etiqueta",                    type: "text" },
      { k: "stat3_value",      label: "Métrica 3 — Valor",                       type: "text" },
      { k: "stat3_label",      label: "Métrica 3 — Etiqueta",                    type: "text" },
      { k: "stat4_value",      label: "Métrica 4 — Valor",                       type: "text" },
      { k: "stat4_label",      label: "Métrica 4 — Etiqueta",                    type: "text" },
      { k: "cta_title_a",      label: "CTA — Línea 1",                           type: "text" },
      { k: "cta_title_b",      label: "CTA — Línea 2 (dorado)",                  type: "text" },
      { k: "cta_button",       label: "CTA — Texto del botón",                   type: "text" },
    ],
  },
  {
    key: "contact",
    label: "Contacto",
    fields: [
      { k: "title",         label: "Título grande",           type: "text" },
      { k: "sub",           label: "Subtítulo",                type: "textarea" },
      { k: "email",         label: "Email público",            type: "text" },
      { k: "location_main", label: "Ubicación principal",      type: "text" },
      { k: "location_sub",  label: "Ubicación — subtítulo",    type: "text" },
      { k: "quote",         label: "Frase inspiracional",      type: "textarea" },
      { k: "quote_author",  label: "Autor de la frase",        type: "text" },
    ],
  },
  {
    key: "footer",
    label: "Footer",
    fields: [
      { k: "tagline",     label: "Tagline (bajo el logo)",          type: "text" },
      { k: "description", label: "Descripción de la empresa",       type: "textarea" },
    ],
  },
];

export const ContentEditor = () => {
  const [data, setData] = useState(null);
  const [section, setSection] = useState("home");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    const { data } = await api.get("/content");
    setData({
      home: data.home || {},
      about: data.about || {},
      contact: data.contact || {},
      footer: data.footer || {},
    });
    setDirty(false);
  };

  useEffect(() => { load(); }, []);

  const set = (sec, key, value) => {
    setData((prev) => ({ ...prev, [sec]: { ...prev[sec], [key]: value } }));
    setDirty(true);
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await api.put("/admin/content", data);
      toast.success("Contenido actualizado. Las traducciones se regenerarán al visitar el sitio.");
      setDirty(false);
    } catch (e) {
      toast.error("Error al guardar");
    } finally { setSaving(false); }
  };

  if (!data) return <div className="text-[#A3A9B2] text-sm py-10">Cargando contenido...</div>;

  const current = SECTIONS.find((s) => s.key === section);

  return (
    <div data-testid="content-editor">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 border-b border-[#C5A059]/15 pb-6">
        <div className="flex flex-wrap gap-2">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              className={`text-[11px] tracking-[0.22em] uppercase font-semibold px-5 py-2 border transition-all ${
                section === s.key
                  ? "bg-[#C5A059] text-[#0A0F16] border-[#C5A059]"
                  : "border-[#C5A059]/25 text-[#A3A9B2] hover:border-[#C5A059] hover:text-white"
              }`}
              data-testid={`content-section-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {dirty && <span className="text-xs text-[#C5A059]">Cambios sin guardar</span>}
          <button
            onClick={load}
            className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase border border-[#C5A059]/30 hover:border-[#C5A059] text-white px-4 py-2"
            data-testid="content-reload"
          >
            <RotateCcw size={12} /> Recargar
          </button>
          <button
            onClick={save}
            disabled={saving || !dirty}
            className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-semibold bg-[#C5A059] text-[#0A0F16] hover:bg-[#E5C07B] disabled:opacity-40 px-5 py-2"
            data-testid="content-save"
          >
            <Save size={12} /> {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>

      <div className="space-y-6 max-w-3xl">
        {current.fields.map((f) => (
          <FieldRow
            key={f.k}
            section={current.key}
            field={f}
            value={data[current.key][f.k] || ""}
            style={data[current.key][`${f.k}_style`] || {}}
            onChangeText={(v) => set(current.key, f.k, v)}
            onChangeStyle={(s) => set(current.key, `${f.k}_style`, s)}
          />
        ))}
      </div>

      <div className="mt-10 p-5 border border-[#C5A059]/20 bg-[#111822] text-xs text-[#A3A9B2] leading-relaxed max-w-3xl">
        <div className="font-semibold text-[#C5A059] mb-2 tracking-[0.18em] uppercase">Nota</div>
        Editá en español. La IA traduce automáticamente al visitar el sitio en portugués, inglés, alemán o francés. Al guardar cambios, el caché de traducciones del campo modificado se borra y se regenera la próxima vez que alguien lo visite en otro idioma.
      </div>
    </div>
  );
};

// ---------- FieldRow with style controls ----------

const SIZE_OPTIONS = [
  { v: "xs",   label: "XS" },
  { v: "sm",   label: "S"  },
  { v: "base", label: "M"  },
  { v: "lg",   label: "L"  },
  { v: "xl",   label: "XL" },
];

const COLOR_OPTIONS = [
  { v: "",      label: "Auto",   swatch: "linear-gradient(135deg,#fff 50%,#C5A059 50%)" },
  { v: "white", label: "Blanco", swatch: "#FFFFFF" },
  { v: "gold",  label: "Dorado", swatch: "#C5A059" },
  { v: "muted", label: "Gris",   swatch: "#A3A9B2" },
];

const ALIGN_OPTIONS = [
  { v: "",       Icon: AlignLeft,   label: "Auto"     },
  { v: "left",   Icon: AlignLeft,   label: "Izquierda" },
  { v: "center", Icon: AlignCenter, label: "Centro"   },
  { v: "right",  Icon: AlignRight,  label: "Derecha"  },
];

const FieldRow = ({ section, field, value, style, onChangeText, onChangeStyle }) => {
  const setStyle = (k, v) => {
    const next = { ...style };
    if (v === "" || v === undefined || v === null) delete next[k];
    else next[k] = v;
    onChangeStyle(next);
  };

  return (
    <div className="border-t border-[#C5A059]/10 pt-6 first:border-t-0 first:pt-0" data-testid={`content-field-${section}-${field.k}`}>
      <label className="text-[10px] tracking-[0.28em] uppercase text-[#C5A059] mb-2 block font-medium">{field.label}</label>

      {field.type === "text" && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChangeText(e.target.value)}
          className="editorial-input w-full py-3 text-base"
        />
      )}
      {field.type === "textarea" && (
        <textarea
          value={value}
          onChange={(e) => onChangeText(e.target.value)}
          rows={3}
          className="editorial-input w-full py-3 text-base resize-none"
        />
      )}
      {field.type === "longtext" && (
        <textarea
          value={value}
          onChange={(e) => onChangeText(e.target.value)}
          rows={10}
          className="editorial-input w-full py-3 text-base"
        />
      )}

      {/* Style controls */}
      <div className="flex flex-wrap gap-x-6 gap-y-3 mt-3 text-[10px]">
        {/* Size */}
        <div className="flex items-center gap-2">
          <span className="text-[#A3A9B2] tracking-[0.2em] uppercase">Tamaño</span>
          {SIZE_OPTIONS.map((s) => (
            <button
              key={s.v}
              type="button"
              onClick={() => setStyle("size", s.v === "base" ? "" : s.v)}
              className={`min-w-[28px] px-2 py-1 border text-[11px] font-semibold tracking-wider transition-colors ${
                (style.size || "base") === s.v
                  ? "bg-[#C5A059] text-[#0A0F16] border-[#C5A059]"
                  : "border-[#C5A059]/25 text-[#A3A9B2] hover:border-[#C5A059] hover:text-white"
              }`}
              data-testid={`style-size-${section}-${field.k}-${s.v}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Color */}
        <div className="flex items-center gap-2">
          <span className="text-[#A3A9B2] tracking-[0.2em] uppercase">Color</span>
          {COLOR_OPTIONS.map((c) => {
            const isActive = (style.color || "") === c.v;
            return (
              <button
                key={c.v || "auto"}
                type="button"
                onClick={() => setStyle("color", c.v)}
                title={c.label}
                className={`w-6 h-6 rounded-full border-2 transition-all ${
                  isActive ? "border-white scale-110" : "border-[#C5A059]/30 hover:border-[#C5A059]"
                }`}
                style={{ background: c.swatch }}
                data-testid={`style-color-${section}-${field.k}-${c.v || "auto"}`}
              />
            );
          })}
        </div>

        {/* Alignment */}
        <div className="flex items-center gap-2">
          <span className="text-[#A3A9B2] tracking-[0.2em] uppercase">Alineación</span>
          {ALIGN_OPTIONS.map((a) => {
            const isActive = (style.align || "") === a.v;
            return (
              <button
                key={a.v || "auto"}
                type="button"
                onClick={() => setStyle("align", a.v)}
                title={a.label}
                className={`w-7 h-7 flex items-center justify-center border transition-colors ${
                  isActive
                    ? "bg-[#C5A059] text-[#0A0F16] border-[#C5A059]"
                    : "border-[#C5A059]/25 text-[#A3A9B2] hover:border-[#C5A059] hover:text-white"
                }`}
                data-testid={`style-align-${section}-${field.k}-${a.v || "auto"}`}
              >
                <a.Icon size={12} />
              </button>
            );
          })}
        </div>

        {/* Reset */}
        {(style.size || style.color || style.align) && (
          <button
            type="button"
            onClick={() => onChangeStyle({})}
            className="text-[10px] tracking-[0.18em] uppercase text-[#A3A9B2] hover:text-[#C5A059] underline"
          >
            Restablecer
          </button>
        )}
      </div>
    </div>
  );
};
